
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { blockRegistry } from '@/lib/email-builder/block-registry';
import { findBlockById, findParentBlock } from '@/lib/email-builder/block-tree-utils';
import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from '@/lib/utils/debounce';
import { registerPendingEdit } from '@/lib/email-builder/pending-edits';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { DocumentConfiguration } from '@/types/email';

// Import block-specific property panels
import {
  TextBlockProperties,
  HeadingBlockProperties,
  ButtonBlockProperties,
  ImageBlockProperties,
  SectionBlockProperties,
  ColumnBlockProperties,
  DividerBlockProperties,
  SpacerBlockProperties,
  HeroBlockProperties,
  FooterBlockProperties,
  SocialBlockProperties,
  VideoBlockProperties,
  QuoteBlockProperties,
  MenuBlockProperties,
  HtmlBlockProperties,
  GroupBlockProperties,
  DocumentProperties,
} from './properties/blocks';

/**
 * Properties Panel Component
 * 
 * Custom Properties Panel Implementation
 * Date: January 27, 2026
 * 
 * Features:
 * - Custom property components (no JSON Forms dependency)
 * - Type-safe block configuration with TypeScript
 * - Debounced updates (500ms) to prevent excessive API calls
 * - Block duplication and deletion actions
 * - Composable shared components for consistent UX
 * - ~155KB smaller bundle size vs JSON Forms
 * 
 * The panel uses a registry pattern to map block types to their
 * specific property panel components.
 * 
 * @see documentation/custom-properties-panel-implementation-plan.md
 */

// Property component registry type
type PropertyComponentProps<T> = {
  config: T;
  onChange: (config: T) => void;
};

// Registry mapping block types to their property components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BLOCK_PROPERTIES_REGISTRY: Record<string, React.ComponentType<PropertyComponentProps<any>>> = {
  text: TextBlockProperties,
  heading: HeadingBlockProperties,
  button: ButtonBlockProperties,
  image: ImageBlockProperties,
  section: SectionBlockProperties,
  column: ColumnBlockProperties,
  divider: DividerBlockProperties,
  spacer: SpacerBlockProperties,
  hero: HeroBlockProperties,
  footer: FooterBlockProperties,
  social: SocialBlockProperties,
  video: VideoBlockProperties,
  quote: QuoteBlockProperties,
  menu: MenuBlockProperties,
  html: HtmlBlockProperties,
  group: GroupBlockProperties,
};

export function PropertiesPanel() {
  const {
    selectedBlockId,
    document,
    blockTree,
    updateBlockConfig,
    updateBlockConfigLocal,
    updateDocumentConfig,
    updateDocumentConfigLocal,
    deleteBlockAction,
    duplicateBlockAction,
    selectBlock,
  } = useEmailBuilderStore();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Find block and related data
  const block = selectedBlockId ? findBlockById(blockTree, selectedBlockId) : null;
  const blockConfig = block ? blockRegistry.get(block.blockType) : null;
  const parentBlock = selectedBlockId ? findParentBlock(blockTree, selectedBlockId) : null;
  const parentBlockConfig = parentBlock ? blockRegistry.get(parentBlock.blockType) : null;

  // Parse current block configuration
  const config = useMemo(() => {
    if (!block) return null;
    try {
      return typeof block.configuration === 'string'
        ? JSON.parse(block.configuration)
        : block.configuration || null;
    } catch (e) {
      console.error('Failed to parse block configuration:', e);
      return null;
    }
  }, [block]);

  // Parse document configuration
  const documentConfig = useMemo<DocumentConfiguration>(() => {
    if (!document) {
      // Default configuration
      return {
        width: 600,
        alignment: 'center',
        backgroundColor: '#ffffff',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
        language: 'en',
      };
    }
    
    try {
      const parsed = document.configuration
        ? (typeof document.configuration === 'string'
          ? JSON.parse(document.configuration)
          : document.configuration)
        : {};
      
      // Merge with defaults to ensure all required fields are present
      const config = {
        width: parsed.width ?? 600,
        alignment: parsed.alignment ?? 'center',
        canvasBackgroundColor: parsed.canvasBackgroundColor,
        backgroundColor: parsed.backgroundColor ?? '#ffffff',
        backgroundImage: parsed.backgroundImage,
        backgroundRepeat: parsed.backgroundRepeat ?? 'no-repeat',
        backgroundPosition: parsed.backgroundPosition ?? 'center',
        padding: parsed.padding ?? { top: 0, right: 0, bottom: 0, left: 0 },
        language: parsed.language ?? 'en',
      };

      return config;
    } catch (e) {
      console.error('Failed to parse document configuration:', e);
      return {
        width: 600,
        alignment: 'center',
        backgroundColor: '#ffffff',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
        language: 'en',
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.id, document?.configuration]);

  // Debounced API sync for blocks (500ms delay)
  const debouncedApiSync = useMemo(
    () =>
      debounce((blockId: string, newConfig: Record<string, unknown>) => {
        updateBlockConfig(blockId, newConfig);
      }, 500),
    [updateBlockConfig]
  );

  // Debounced API sync for document config (500ms delay)
  const debouncedDocumentApiSync = useMemo(
    () =>
      debounce((newConfig: DocumentConfiguration) => {
        updateDocumentConfig(newConfig);
      }, 500),
    [updateDocumentConfig]
  );

  // Block whose edit is waiting in debouncedApiSync. The debouncer keeps only the latest call, so
  // an edit to another block must flush it first or the earlier block's change is never sent.
  const pendingBlockIdRef = useRef<string | null>(null);

  // Send pending edits on save/checkpoint (via the registry) and when the panel unmounts.
  useEffect(() => {
    const flushAll = () => {
      debouncedApiSync.flush();
      debouncedDocumentApiSync.flush();
    };
    const unregister = registerPendingEdit(flushAll);
    return () => {
      flushAll();
      unregister();
    };
  }, [debouncedApiSync, debouncedDocumentApiSync]);

  const handleConfigChange = useCallback(
    (newConfig: Record<string, unknown>) => {
      // Blocks still being created (temporary IDs) are fine: the store waits for their real ID
      if (!selectedBlockId) {
        return;
      }

      if (pendingBlockIdRef.current && pendingBlockIdRef.current !== selectedBlockId) {
        debouncedApiSync.flush();
      }

      // Ignore calls that don't change anything (property components may echo the current
      // config). Compared with the block as it is now, not the last value sent: after an undo or a
      // reload, setting a value again is a real change.
      if (JSON.stringify(newConfig) === JSON.stringify(config)) {
        return;
      }

      try {
        // 1. Update local state immediately (instant canvas update)
        updateBlockConfigLocal(selectedBlockId, newConfig);
        
        // 2. Debounce the API call (sync to server after 500ms)
        pendingBlockIdRef.current = selectedBlockId;
        debouncedApiSync(selectedBlockId, newConfig);
      } catch (error) {
        console.error('Error updating block configuration:', error);
      }
    },
    [config, debouncedApiSync, selectedBlockId, updateBlockConfigLocal]
  );

  const handleDocumentConfigChange = useCallback(
    (newConfig: DocumentConfiguration) => {
      // Ignore calls that don't change anything
      if (JSON.stringify(newConfig) === JSON.stringify(documentConfig)) {
        return;
      }

      try {
        // 1. Update local state immediately (instant canvas update)
        updateDocumentConfigLocal(newConfig);
        
        // 2. Debounce the API call (sync to server after 500ms)
        debouncedDocumentApiSync(newConfig);
      } catch (error) {
        console.error('Error updating document configuration:', error);
      }
    },
    [documentConfig, debouncedDocumentApiSync, updateDocumentConfigLocal]
  );

  // No block selected - show document properties
  if (!selectedBlockId || !block || !config) {
    if (!document) {
      return (
        <div className="flex h-full items-center justify-center p-4 text-center">
          <div>
            <p className="text-sm text-muted-foreground">
              No document loaded
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="lw-properties-panel flex h-full flex-col">
        {/* Header */}
        <div className="lw-properties-panel-header border-b border-border px-3 py-2">
          <h3 className="text-xs font-semibold leading-tight">Document Settings</h3>
          <p className="text-[10px] text-muted-foreground leading-tight">Configure email shell properties</p>
        </div>

        {/* Document Properties Form */}
        <div className="lw-properties-panel-form flex-1 overflow-y-auto px-3 py-2">
          <DocumentProperties
            config={documentConfig}
            onChange={handleDocumentConfigChange}
          />
        </div>
      </div>
    );
  }

  // Get the appropriate property component for this block type
  const PropertyComponent = BLOCK_PROPERTIES_REGISTRY[block.blockType];

  return (
    <div className="lw-properties-panel flex h-full flex-col">
      {/* Header — block name + description + icon actions in one compact bar */}
      <div className="lw-properties-panel-header flex items-start justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex-1 min-w-0">
          {/* Parent breadcrumb */}
          {parentBlock && (
            <button
              onClick={() => selectBlock(parentBlock.id)}
              className="mb-0.5 flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg className="h-2.5 w-2.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="truncate">{parentBlockConfig?.displayName || parentBlock.blockType}</span>
            </button>
          )}
          <h3 className="text-xs font-semibold leading-tight truncate">
            {blockConfig?.displayName || block.blockType}
          </h3>
          <p className="text-[10px] text-muted-foreground leading-tight truncate">
            {blockConfig?.description || 'Configure block properties'}
          </p>
        </div>

        {/* Icon-only action buttons */}
        <div className="flex items-center gap-0.5 shrink-0 mt-0.5">
          <button
            onClick={() => duplicateBlockAction(selectedBlockId)}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title="Duplicate block"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            title="Delete block"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Block?"
        description={`Are you sure you want to delete this ${blockConfig?.displayName || block.blockType} block? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => deleteBlockAction(selectedBlockId)}
      />

      {/* Properties Form */}
      <div className="lw-properties-panel-form flex-1 overflow-y-auto px-3 py-2">
        {!PropertyComponent ? (
          // Fallback for unknown block types
          <div className="rounded-md border border-dashed border-border p-4 text-center">
            <p className="text-sm text-muted-foreground">
              No property panel available for this block type.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Block type: <code className="rounded bg-muted px-1">{block.blockType}</code>
            </p>
          </div>
        ) : (
          // Render the appropriate property component
          <PropertyComponent config={config} onChange={handleConfigChange} />
        )}
      </div>
    </div>
  );
}

